import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { lockBodyScroll } from '../../utils/bodyScrollLock';
import { playSound } from '../../utils/utils';
import DieMiniature from './DieMiniature';
import DamageHitSplat from './DamageHitSplat';
import { getAttackDieFaceValues } from './dice';
import {
  CORE_FINALE_DURATION_MS,
  createCoreFinaleScene,
  getCoreFinaleFrame,
} from './coreFinaleScene';
import './CoreFinalBlow.css';

const CHAPTERS = [
  {
    at: 0,
    label: 'THE LAST SIGNAL',
    title: 'One light remains.',
    detail: 'The Core reaches for the last thing it could never corrupt.',
  },
  {
    at: 3.2,
    label: 'EVERY WORLD. ONE THROW.',
    title: 'COREBREAKER',
    detail: 'The darkness breaks from within.',
  },
  {
    at: 5.6,
    label: 'THE SIGNAL IS FREE',
    title: 'Let there be stars.',
    detail: 'Across the galaxies, the light finds its way home.',
    restoring: true,
  },
  {
    at: 9.3,
    label: 'ALL TEN SECTORS RESTORED',
    title: 'The universe is still here.',
    detail: 'Because of you.',
    restoring: true,
    restored: true,
  },
];
const SOUNDS = [
  [0.15, '/sounds/glitch_core_misc_sound_2.mp3'],
  [1.35, '/sounds/dice-increase-power.mp3'],
  [2.65, '/sounds/space-synth.mp3'],
  [3.2, '/sounds/treasure-chest-slice-open.mp3'],
  [4.15, '/sounds/boss-dies.mp3'],
  [4.25, '/sounds/large-underwater-explosion-sound.mp3'],
  [5.7, '/sounds/whoosh-cinematic-sound.mp3'],
];
const FALLBACK_ARMS = [
  'M -30 -12 Q -72 -24 -86 -64',
  'M -39 0 Q -81 24 -97 0',
  'M -22 20 Q -72 53 -66 84',
  'M 30 -12 Q 72 -24 86 -64',
  'M 39 0 Q 81 24 97 0',
  'M 22 20 Q 72 53 66 84',
];

const CoreFinalBlow = ({
  dieId,
  damage,
  critical = false,
  faceValues,
  reduceMotion = false,
  impactComplete = false,
  onHandoffComplete,
  onImpact,
  onComplete,
}) => {
  const hostRef = useRef(null);
  const dialogRef = useRef(null);
  const skipRef = useRef(null);
  const completeRef = useRef(onComplete);
  const impactRef = useRef(onImpact);
  const handoffRef = useRef(onHandoffComplete);
  const finishedRef = useRef(false);
  const [fallback, setFallback] = useState(false);
  const [impacted, setImpacted] = useState(impactComplete);
  const [chapterIndex, setChapterIndex] = useState(
    reduceMotion ? CHAPTERS.length - 1 : impactComplete ? 1 : 0
  );
  completeRef.current = onComplete;
  impactRef.current = onImpact;
  handoffRef.current = onHandoffComplete;
  const finish = () => {
    if (finishedRef.current) return;
    finishedRef.current = true;
    completeRef.current?.();
  };
  const finishRef = useRef(finish);
  finishRef.current = finish;

  useEffect(() => {
    const releaseScroll = lockBodyScroll();
    const root = document.getElementById('root');
    const previousInert = root?.inert;
    const previousHidden = root?.getAttribute('aria-hidden');
    const previousFocus = document.activeElement;
    dialogRef.current?.focus({ preventScroll: true });
    if (root) {
      root.inert = true;
      root.setAttribute('aria-hidden', 'true');
    }
    const scene = createCoreFinaleScene(hostRef.current, {
      dieId,
      damage,
      faceValues,
      reduceMotion,
    });
    const initialTime = reduceMotion ? 9.9 : impactComplete ? 3.2 : 0;
    const initialFrame = getCoreFinaleFrame(initialTime);
    Object.entries(initialFrame).forEach(([key, value]) =>
      dialogRef.current?.style.setProperty(`--finale-${key}`, String(value))
    );
    scene?.update(initialTime, initialFrame);
    setFallback(!scene);
    const duration = reduceMotion
      ? 3200
      : CORE_FINALE_DURATION_MS - initialTime * 1000;
    let frameId,
      start = null,
      currentChapter = reduceMotion
        ? CHAPTERS.length - 1
        : impactComplete
        ? 1
        : 0;
    let lostContext = false;
    let impacted = impactComplete;
    let handedOff = false;
    const played = new Set(
      SOUNDS.flatMap(([at], index) => (at < initialTime ? [index] : []))
    );
    const animate = (now) => {
      if (finishedRef.current) return;
      if (start === null) start = now;
      const elapsed = (now - start) / 1000;
      const time = reduceMotion ? 9.9 : initialTime + elapsed;
      const handoff =
        !impactComplete || reduceMotion ? 1 : Math.min(1, elapsed / 0.32);
      dialogRef.current?.style.setProperty('--finale-handoff', String(handoff));
      if (!handedOff && handoff === 1) {
        handedOff = true;
        handoffRef.current?.();
      }
      const frame = getCoreFinaleFrame(time);
      if (!impacted && time >= 3.2) {
        impacted = true;
        setImpacted(true);
        impactRef.current?.();
      }
      Object.entries(frame).forEach(([key, value]) =>
        dialogRef.current?.style.setProperty(`--finale-${key}`, String(value))
      );
      dialogRef.current?.style.setProperty(
        '--finale-progress',
        String(Math.min(1, (elapsed * 1000) / duration))
      );
      if (scene && !lostContext && !scene.update(time, frame)) {
        lostContext = true;
        setFallback(true);
      }
      let nextChapter = 0;
      CHAPTERS.forEach((chapter, index) => {
        if (time >= chapter.at) nextChapter = index;
      });
      if (nextChapter !== currentChapter) {
        currentChapter = nextChapter;
        setChapterIndex(nextChapter);
      }
      if (!reduceMotion)
        SOUNDS.forEach(([at, sound], index) => {
          if (time >= at && !played.has(index)) {
            played.add(index);
            playSound(sound, { preventOverlap: true });
          }
        });
      if (elapsed * 1000 >= duration) finishRef.current();
      else frameId = requestAnimationFrame(animate);
    };
    frameId = requestAnimationFrame(animate);
    const failsafe = setTimeout(() => finishRef.current(), duration + 3000);
    return () => {
      clearTimeout(failsafe);
      cancelAnimationFrame(frameId);
      scene?.dispose();
      releaseScroll();
      if (root) {
        root.inert = previousInert;
        if (previousHidden === null) root.removeAttribute('aria-hidden');
        else root.setAttribute('aria-hidden', previousHidden);
      }
      if (previousFocus?.isConnected)
        previousFocus.focus?.({ preventScroll: true });
    };
  }, [dieId, damage, faceValues, reduceMotion, impactComplete]);

  const chapter = CHAPTERS[chapterIndex];
  const fallbackFaces = getAttackDieFaceValues(dieId, damage, faceValues);
  return createPortal(
    <section
      ref={dialogRef}
      style={{ '--finale-handoff': impactComplete && !reduceMotion ? 0 : 1 }}
      className={`coreboundCoreFinale ${fallback ? 'is-fallback' : ''} ${
        reduceMotion ? 'is-reduced' : ''
      } ${chapter.restoring ? 'is-restoring' : ''} ${
        chapter.restored ? 'is-restored' : ''
      }`}
      role="dialog"
      aria-modal="true"
      aria-labelledby="corebound-finale-title"
      aria-describedby="corebound-finale-description"
      tabIndex={-1}
      onKeyDown={(event) => {
        if (event.key === 'Escape') {
          event.preventDefault();
          finish();
        }
        if (event.key === 'Tab') {
          event.preventDefault();
          skipRef.current?.focus();
        }
      }}
    >
      <div
        className="coreboundCoreFinaleScene"
        ref={hostRef}
        aria-hidden="true"
      />
      {fallback && (
        <div className="coreboundFinaleFallback" aria-hidden="true">
          <div className="coreboundFinaleFallbackSky" />
          <svg
            className="coreboundFinaleFallbackCore"
            viewBox="-110 -110 220 220"
          >
            <circle r="93" fill="none" stroke="#ff6f9a" strokeWidth="0.5" />
            <circle r="72" fill="none" stroke="#ffad68" strokeWidth="0.5" />
            <g fill="none" stroke="#ef4276" strokeWidth="3">
              {FALLBACK_ARMS.map((path, index) => (
                <path key={index} d={path} />
              ))}
            </g>
            <path
              fill="#b9204f"
              d="M -16 -27 -33 -71 -3 -35 14 -73 27 -28 72 -45 38 -7 81 19 34 21 38 68 2 40 -40 70 -31 25 -81 18 -39 -7 -72 -45Z"
            />
            <path
              fill="#ff4e79"
              d="M 0 -27 C -28 -60 -72 -20 0 42 C 72 -20 28 -60 0 -27Z"
            />
            <path fill="#b9204f" d="M 0 -27 C 28 -60 72 -20 0 42Z" />
            <path
              fill="#ffe7a6"
              stroke="#270f2b"
              strokeWidth="2"
              d="M -34 -16 -9 -5 -20 1Z M 34 -16 9 -5 20 1Z"
            />
          </svg>
          <div className="coreboundFinaleFallbackDie">
            <DieMiniature dieId={dieId} faceValues={fallbackFaces} />
          </div>
          <div className="coreboundFinaleFallbackShards">
            {Array.from({ length: 16 }, (_, index) => (
              <i
                key={index}
                style={{
                  '--fragment-angle': `${index * 22.5}deg`,
                  '--fragment-distance': `${180 + (index % 4) * 40}px`,
                }}
              />
            ))}
          </div>
        </div>
      )}
      <div className="coreboundFinaleVignette" aria-hidden="true" />
      <div className="coreboundFinaleFlash" aria-hidden="true" />
      {impacted && (
        <DamageHitSplat
          hitId="final-blow"
          damage={damage}
          target="Core"
          critical={critical}
          reduceMotion={reduceMotion}
        />
      )}
      <header className="coreboundFinaleHeader" aria-hidden="true">
        <span>
          <i /> RIFT LOOM <b>/ FINAL SIGNAL</b>
        </span>
        <span className="coreboundFinaleDamage">
          FINAL HIT <b>{damage}</b>
        </span>
      </header>
      <div
        key={chapterIndex}
        className="coreboundFinaleCopy"
        aria-live="polite"
        aria-atomic="true"
      >
        <span>{chapter.label}</span>
        <h1 id="corebound-finale-title">{chapter.title}</h1>
        <p id="corebound-finale-description">{chapter.detail}</p>
      </div>
      <div
        className="coreboundFinaleSectors"
        aria-label="All ten sectors restored"
        aria-hidden={!chapter.restoring}
      >
        <div>
          {Array.from({ length: 10 }, (_, index) => (
            <i key={index} style={{ '--sector': index }} />
          ))}
        </div>
        <span>THE STARS ARE COMING BACK</span>
      </div>
      <div className="coreboundFinaleProgress" aria-hidden="true">
        <i />
      </div>
      <button
        ref={skipRef}
        type="button"
        className="coreboundFinaleSkip"
        onClick={finish}
      >
        {chapter.restored ? 'Continue to victory' : 'Skip to results'}{' '}
        <span aria-hidden="true">→</span>
      </button>
    </section>,
    document.body
  );
};

export default CoreFinalBlow;
